import { MESSAGE_AUTHOR_TYPE } from '../../models/messages/Messages'

import type { LyraLibraryResource, Message } from '../../models/messages/Messages'

export enum CHAT_MESSAGE_SENDER_V2 {
  UNSPECIFIED = 0,
  CLIENT = 1,
  ASSISTANT = 2,
}

export enum CHAT_MESSAGE_CONTENT_TYPE_V2 {
  UNSPECIFIED = 0,
  TEXT = 1,
  MARKDOWN = 2,
}

export enum CHAT_MESSAGE_TYPE_V2 {
  UNSPECIFIED = 0,
  GREETING = 1,
  CHAT = 2,
  SYSTEM = 3,
  HIGH_ALERT_TIMEOUT = 4,
}

export enum HIGH_ALERT_RISK_LEVEL_V2 {
  UNSPECIFIED = 0,
  YELLOW = 1,
  AMBER = 2,
  RED = 3,
}

export type ChatMessageV2 = {
  message_id: string
  conversation_id: string
  content: {
    type: CHAT_MESSAGE_CONTENT_TYPE_V2
    text: string
  }
  sender: CHAT_MESSAGE_SENDER_V2
  timestamp: string
  metadata?: Record<string, unknown> | null
  message_type: CHAT_MESSAGE_TYPE_V2
  lyra_library_resources?: LyraLibraryResource[]
}

export type ChatResponseV2 = {
  message: ChatMessageV2
  high_alert_risk_level?: HIGH_ALERT_RISK_LEVEL_V2
  high_risk_assessment?: Record<string, unknown> | null
  guardrail_type?: number
}

export type ListChatHistoryResponseV2 = {
  conversation_id: string
  messages: ChatMessageV2[]
  total_messages: number
  has_more: boolean
}

export const V2_CHAT_HISTORY_PAGE_SIZE = 50

export type ChatHistoryPageV2 = {
  conversationId: string
  messages: Message[]
  hasMore: boolean
  before: string | null
}

export type SubmittedMessageV2 = {
  body: string
  clientId: string
  metadata?: Record<string, unknown> | null
}

const getClientMessageAttributes = (submittedMessage: SubmittedMessageV2) =>
  JSON.stringify({
    message_type: 'direct',
    from: submittedMessage.clientId,
    author_type: MESSAGE_AUTHOR_TYPE.PATIENT,
    metadata: submittedMessage.metadata ?? null,
  })

export const createPendingMessageV2 = (
  conversationId: string,
  submittedMessage: SubmittedMessageV2,
  messageId: string,
  dateCreated: string,
): Message => ({
  attributes: getClientMessageAttributes(submittedMessage),
  body: submittedMessage.body,
  conversationId,
  date_created: dateCreated,
  message_id: messageId,
  sendStatus: 'sending',
})

const HIGH_ALERT_RISK_LEVEL_NAMES: Partial<
  Record<HIGH_ALERT_RISK_LEVEL_V2, { highAlertRiskLevel: string; riskLevel: string }>
> = {
  [HIGH_ALERT_RISK_LEVEL_V2.YELLOW]: {
    highAlertRiskLevel: 'RISK_LEVEL_YELLOW',
    riskLevel: 'YELLOW',
  },
  [HIGH_ALERT_RISK_LEVEL_V2.AMBER]: {
    highAlertRiskLevel: 'RISK_LEVEL_AMBER',
    riskLevel: 'AMBER',
  },
  [HIGH_ALERT_RISK_LEVEL_V2.RED]: {
    highAlertRiskLevel: 'RISK_LEVEL_RED',
    riskLevel: 'RED',
  },
}

const getAssistantMessageMetadata = (
  message: ChatMessageV2,
  highAlertRiskLevel?: HIGH_ALERT_RISK_LEVEL_V2,
): Record<string, unknown> => {
  const highAlertRiskLevelNames = highAlertRiskLevel ? HIGH_ALERT_RISK_LEVEL_NAMES[highAlertRiskLevel] : undefined

  return {
    ...(message.metadata ?? {}),
    gen_ai_message_id: message.message_id,
    ...(highAlertRiskLevelNames
      ? {
          high_alert_risk_level: highAlertRiskLevelNames.highAlertRiskLevel,
          risk_level: highAlertRiskLevelNames.riskLevel,
        }
      : {}),
    ...(message.lyra_library_resources?.length ? { attached_resources: message.lyra_library_resources } : {}),
  }
}

const assertConversationIdMatches = (
  actualConversationId: string,
  expectedConversationId: string,
  source: 'response' | 'message',
) => {
  if (!expectedConversationId) {
    throw new Error('A requested conversation ID is required to map V2 chat history')
  }
  if (!actualConversationId) {
    throw new Error(`The V2 chat history ${source} is missing its conversation ID`)
  }
  if (actualConversationId !== expectedConversationId) {
    throw new Error(
      `The V2 chat history ${source} conversation ID "${actualConversationId}" does not match the requested conversation ID "${expectedConversationId}"`,
    )
  }
}

const getHistoryMessageAuthorType = (sender: CHAT_MESSAGE_SENDER_V2): MESSAGE_AUTHOR_TYPE => {
  switch (sender) {
    case CHAT_MESSAGE_SENDER_V2.CLIENT:
      return MESSAGE_AUTHOR_TYPE.PATIENT
    case CHAT_MESSAGE_SENDER_V2.ASSISTANT:
      return MESSAGE_AUTHOR_TYPE.AI_COACH
    default:
      throw new Error(`Unsupported V2 chat history message sender: ${sender}`)
  }
}

const getHistoryMessageBody = (message: ChatMessageV2): string => {
  switch (message.content.type) {
    case CHAT_MESSAGE_CONTENT_TYPE_V2.TEXT:
    case CHAT_MESSAGE_CONTENT_TYPE_V2.MARKDOWN:
      return message.content.text
    default:
      throw new Error(`Unsupported V2 chat history message content type: ${message.content.type}`)
  }
}

/**
 * Normalizes one V2 history message into the legacy shape consumed by member
 * web and mobile. The caller-provided conversation ID is the request identity,
 * so a mismatched wire message is rejected instead of being assigned to the
 * wrong conversation.
 */
export const mapChatHistoryMessageV2ToMessage = (message: ChatMessageV2, expectedConversationId: string): Message => {
  assertConversationIdMatches(message.conversation_id, expectedConversationId, 'message')

  const authorType = getHistoryMessageAuthorType(message.sender)
  const metadata =
    authorType === MESSAGE_AUTHOR_TYPE.AI_COACH ? getAssistantMessageMetadata(message) : (message.metadata ?? null)

  return {
    attributes: JSON.stringify({
      message_type: 'direct',
      author_type: authorType,
      metadata,
    }),
    body: getHistoryMessageBody(message),
    conversationId: expectedConversationId,
    date_created: message.timestamp,
    message_id: message.message_id,
    sendStatus: 'sent',
  }
}

/**
 * Normalizes one chronological V2 history page. `before` is the oldest raw
 * server timestamp, including when that row is unsupported by the UI. This
 * consumes skipped rows so older supported messages remain reachable.
 */
export const mapChatHistoryResponseV2ToPage = (
  response: ListChatHistoryResponseV2,
  expectedConversationId: string,
): ChatHistoryPageV2 => {
  assertConversationIdMatches(response.conversation_id, expectedConversationId, 'response')

  response.messages.forEach((message) => {
    assertConversationIdMatches(message.conversation_id, expectedConversationId, 'message')
  })

  const supportedMessages = response.messages.filter(
    (message) =>
      (message.sender === CHAT_MESSAGE_SENDER_V2.CLIENT || message.sender === CHAT_MESSAGE_SENDER_V2.ASSISTANT) &&
      (message.content.type === CHAT_MESSAGE_CONTENT_TYPE_V2.TEXT ||
        message.content.type === CHAT_MESSAGE_CONTENT_TYPE_V2.MARKDOWN),
  )

  return {
    conversationId: expectedConversationId,
    messages: supportedMessages.map((message) => mapChatHistoryMessageV2ToMessage(message, expectedConversationId)),
    hasMore: response.has_more,
    before: response.messages[0]?.timestamp ?? null,
  }
}

/**
 * Normalizes the V2 GenAI response and the submitted client message into the
 * legacy message shape consumed by member web. When a pending client message
 * is provided, its local identity and send time are preserved as it is marked
 * sent.
 */
export const mapChatResponseV2ToMessages = (
  response: ChatResponseV2,
  submittedMessage: SubmittedMessageV2,
  pendingClientMessage?: Message,
): Message[] => {
  const { message, high_alert_risk_level: highAlertRiskLevel } = response
  const dateCreated = message.timestamp || new Date().toISOString()
  const assistantMetadata = getAssistantMessageMetadata(message, highAlertRiskLevel)

  const clientMessage: Message = pendingClientMessage
    ? { ...pendingClientMessage, sendStatus: 'sent' }
    : {
        attributes: getClientMessageAttributes(submittedMessage),
        body: submittedMessage.body,
        conversationId: message.conversation_id,
        date_created: dateCreated,
        message_id: `${message.message_id}-client`,
        sendStatus: 'sent',
      }

  const assistantMessage: Message = {
    attributes: JSON.stringify({
      message_type: 'direct',
      author_type: MESSAGE_AUTHOR_TYPE.AI_COACH,
      metadata: assistantMetadata,
    }),
    body: message.content.text,
    conversationId: message.conversation_id,
    date_created: dateCreated,
    message_id: message.message_id,
    sendStatus: 'sent',
  }

  return [clientMessage, assistantMessage]
}
